import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { availableInterfaceThemes, type LanguageCode, type OwnerProfile } from '@kairelo/shared';
import { auth, profile as profileApi } from '../lib/api.js';
import { getInterfaceTheme } from '../theme/registry.js';
import { Icon } from '../components/Icon.tsx';
import { Button, Card, Field, Loading, Notice, useToast } from '../components/ui.tsx';
import { LanguageSelect } from '../components/LanguageSelect.tsx';
import { detectCapability, type TranslationAvailability } from '../lib/translation.js';
import { isInterfaceTranslated } from '../i18n/catalogues.js';
import { I18nProvider, useT } from '../i18n/I18n.tsx';

/**
 * Account settings.
 *
 * Reached from the messenger's overflow menu, as it is in the clients this
 * interface follows. Profile and branding keep their own pages; this is the
 * account-level shelf above them.
 */
export function Settings() {
  const [language, setLanguage] = useState<LanguageCode>('en');
  return (
    <I18nProvider language={language}>
      <SettingsInner onLanguage={setLanguage} />
    </I18nProvider>
  );
}

function SettingsInner({ onLanguage }: { onLanguage: (language: LanguageCode) => void }) {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();

  const [profile, setProfile] = useState<OwnerProfile | null>(null);
  const [saving, setSaving] = useState(false);
  const [availability, setAvailability] = useState<TranslationAvailability>('unavailable');

  useEffect(() => {
    profileApi
      .get()
      .then((loaded) => {
        setProfile(loaded);
        if (loaded.preferredLanguage) onLanguage(loaded.preferredLanguage);
      })
      .catch(() => {});
  }, [onLanguage]);

  useEffect(() => {
    if (!profile?.preferredLanguage) return;
    void detectCapability({
      sourceLanguage: profile.preferredLanguage === 'en' ? 'es' : 'en',
      targetLanguage: profile.preferredLanguage,
    }).then((capability) => setAvailability(capability.availability));
  }, [profile?.preferredLanguage]);

  if (!profile) {
    return (
      <div className="page page--narrow">
        <Loading>{t('common.loading')}</Loading>
      </div>
    );
  }

  const saveLanguage = (language: LanguageCode) => {
    setSaving(true);
    void profileApi
      .update({
        displayName: profile.displayName,
        headline: profile.headline,
        bio: profile.bio,
        preferredLanguage: language,
      })
      .then((updated) => {
        setProfile(updated);
        onLanguage(language);
        toast('success', t('settings.languageSaved'));
      })
      .catch(() => toast('error', t('settings.languageFailed')))
      .finally(() => setSaving(false));
  };

  const activeTheme = getInterfaceTheme(profile.preferredInterface);

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <span className="tile tile--accent" aria-hidden="true">
          <Icon name="settings" size={20} />
        </span>
        <div className="page__title">
          <h1>{t('settings.title')}</h1>
          <p>{profile.displayName}</p>
        </div>
      </header>

      <Card title={t('settings.languageTitle')} description={t('settings.languageSubtitle')}>
        <Field
          label={t('settings.languageLabel')}
          htmlFor="language"
          hint={t('settings.languageHint')}
        >
          {({ id, describedBy }) => (
            <LanguageSelect
              id={id}
              describedBy={describedBy}
              disabled={saving}
              value={profile.preferredLanguage ?? 'en'}
              onChange={saveLanguage}
            />
          )}
        </Field>

        {profile.preferredLanguage && !isInterfaceTranslated(profile.preferredLanguage) ? (
          <Notice tone="warn" title={t('settings.uiEnglish')}>
            {t('settings.uiEnglishNote')}
          </Notice>
        ) : null}

        {availability === 'unavailable' ? (
          <Notice tone="warn" title={t('settings.interfaceUnavailableTitle')}>
            {t('settings.interfaceUnavailableBody')}
          </Notice>
        ) : (
          <Notice tone="success" title={t('settings.translationAvailableTitle')}>
            {t('settings.translationAvailableBody')}
            {availability === 'mock' ? ` (${t('message.testAdapter')})` : ''}
          </Notice>
        )}
      </Card>

      <Card
        title={t('settings.interfaceTitle')}
        description="How the chat looks. More styles are planned; only one is available today."
      >
        <Field label={t('settings.interfaceLabel')} htmlFor="interface">
          {({ id }) => (
            <select id={id} value={profile.preferredInterface} disabled>
              {availableInterfaceThemes.map((themeId) => (
                <option key={themeId} value={themeId}>
                  {getInterfaceTheme(themeId).label}
                </option>
              ))}
            </select>
          )}
        </Field>
        <p className="field__hint">{activeTheme.description}</p>
      </Card>

      <Card title={t('settings.accountTitle')}>
        <div className="stack">
          <Link className="btn" to="/app/profile">
            <Icon name="user" />
            {t('common.profile')}
          </Link>
          <Link className="btn" to="/app/branding">
            <Icon name="palette" />
            {t('common.branding')}
          </Link>
          <Button
            variant="danger"
            icon="logout"
            onClick={() => void auth.signOut().finally(() => navigate('/login'))}
          >
            {t('common.signOut')}
          </Button>
        </div>
      </Card>

      <p className="text-sm muted" style={{ marginTop: 'var(--s-5)' }}>
        <Link to="/app/conversations">{t('settings.backToConversations')}</Link>
      </p>
    </div>
  );
}
